import { Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {
  form: FormGroup;
  showPassword = false;
  loading = false;
  loginError: string | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
  ) {
    this.form = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required, Validators.minLength(6)]),
    });
  }

  get emailInvalid(): boolean {
    const ctrl = this.form.get('email')!;
    return ctrl.touched && ctrl.invalid;
  }

  get passwordInvalid(): boolean {
    const ctrl = this.form.get('password')!;
    return ctrl.touched && ctrl.invalid;
  }

  async login() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.loginError = null;

    const email = this.form.value['email'].trim();
    const password = this.form.value['password'].trim();

    try {
      const credential = await this.authService.login(email, password);
      await this.handleSuccess(credential);
    } catch (err: any) {
      this.loginError = this.translateError(err.code);
      this.form.get('password')!.reset();
    } finally {
      this.loading = false;
    }
  }

  async loginWithGoogle() {
    this.loading = true;
    this.loginError = null;
    try {
      const credential = await this.authService.loginWithGoogle();
      await this.handleSuccess(credential);
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User closed the popup — no error shown
      } else {
        this.loginError = this.translateError(err.code);
      }
    } finally {
      this.loading = false;
    }
  }

  async loginWithApple() {
    this.loading = true;
    this.loginError = null;
    try {
      const credential = await this.authService.loginWithApple();
      await this.handleSuccess(credential);
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User closed the popup — no error shown
      } else {
        this.loginError = this.translateError(err.code);
      }
    } finally {
      this.loading = false;
    }
  }

  private async handleSuccess(_credential: any) {
    const claims = await this.authService.getTokenClaims();
    const hasRoles = claims && Array.isArray(claims['roles']) && (claims['roles'] as string[]).length > 0;

    if (!hasRoles) {
      await this.authService.logout();
      this.loginError = 'No tienes permisos para acceder a esta plataforma.';
      return;
    }

    this.router.navigate(['/statistics']);
  }

  private translateError(code: string): string {
    switch (code) {
      case 'auth/invalid-email':
        return 'El correo electrónico no es válido.';
      case 'auth/user-disabled':
        return 'Esta cuenta ha sido desactivada.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Correo o contraseña incorrectos.';
      case 'auth/too-many-requests':
        return 'Demasiados intentos fallidos. Inténtalo más tarde.';
      case 'auth/network-request-failed':
        return 'Error de conexión. Comprueba tu red.';
      default:
        return 'Ha ocurrido un error. Inténtalo de nuevo.';
    }
  }
}
